import { RouterStore } from '@estee/elc-router';
import { AnyObject, BrowserStorage } from '@estee/elc-universal-utils';

import { StorageKey } from '~constants/Constants';
import { SignInType } from '~constants/SignInTypeEnum';
import { SOCIAL_OAUTH_RESPONSE_TYPE } from '~constants/SocialConstants';
import { ISocialProvider } from '~domain/entities/Config';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { UserRepository } from '~repositories/UserRepository';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/RampService';
import { generateSocialState, getSocialRedirectUri } from '~utils/SocialUtils';

export class SocialSignInClickedUseCase {
    private routerStore: RouterStore;
    private configRepository: ConfigRepository;
    private userRepository: UserRepository;
    private socialProvider: SignInType;
    private overrideParams?: AnyObject;
    constructor(provider: SignInType, overrideParams?: AnyObject) {
        this.routerStore = diContainer.get(serviceNames.routerStore);
        this.configRepository = diContainer.get(serviceNames.configRepository);
        this.userRepository = diContainer.get(serviceNames.userRepository);
        this.socialProvider = provider;
        this.overrideParams =
            overrideParams ?? this.userRepository.socialSignInState.extraParamsCollection[provider];
    }

    execute = async () => {
        const {
            signInEventSource,
            userFormStore: {
                values: { storeLocation, loyaltySignupPosTransId }
            }
        } = this.userRepository;
        const selectedProvider = this.configRepository.getSocialProviderByName(this.socialProvider);
        if (selectedProvider) {
            const { displayName, providerName } = selectedProvider;

            const state = generateSocialState(
                this.socialProvider,
                storeLocation,
                loyaltySignupPosTransId
            );
            BrowserStorage.setItem(StorageKey.SOCIAL_STATE, state, 'localStorage');
            BrowserStorage.setItem(
                StorageKey.SOCIAL_EVENT_SOURCE,
                signInEventSource,
                'localStorage'
            );
            BrowserStorage.setItem(
                StorageKey.SOCIAL_DISPLAY_NAME,
                displayName || providerName,
                'localStorage'
            );

            const oauthUrl = this.buildOauthUrl(selectedProvider, state);

            this.routerStore.changeLocation(oauthUrl);
        }
    };

    buildOauthUrl = (provider: ISocialProvider, state: string) => {
        const {
            oauthConfig: {
                responseType,
                scope,
                clientId,
                uiLocales = null,
                botPrompt = null,
                requestUrl
            }
        } = provider;
        const useMockServer =
            process.env.NODE_ENV === 'development' &&
            process.env.USE_MOCK_SERVER?.toLowerCase() === 'true';

        const searchParams = new URLSearchParams();
        Object.entries({
            client_id: clientId,
            state: encodeURI(state.toString()),
            redirect_uri: getSocialRedirectUri(),
            response_type: responseType || SOCIAL_OAUTH_RESPONSE_TYPE,
            scope,
            ui_locales: uiLocales,
            bot_prompt: botPrompt,
            ...this.overrideParams
        }).forEach(([key, value]) => {
            value && searchParams.set(key, value);
        });

        if (useMockServer) {
            return (
                `${process.env.MOCK_SOCIAL_PROVIDER_URL}/authorize?${searchParams.toString()}` || ''
            );
        }

        return `${requestUrl}?${searchParams.toString()}`;
    };
}
